import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  Skeleton,
  Autocomplete,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import FactoryIcon from "@mui/icons-material/Factory";

import PlantModuleNav from "../../../../../components/PlantModuleNav";
import NavTabs from "../../../../../components/Tabs/NavTabs";
import { SpecTab } from "./StructureModelingTabs/SpecTab";
import { EstimationTab } from "./StructureModelingTabs/EstimationTab";
// Spec Library tab removed from Structure — allCombinationTable is built in
// structureModelingContext, so Estimation gets its dropdown options without it.
// import { PartLib } from "./StructureModelingTabs/PartLibTab";
import { ModelingTab } from "./StructureModelingTabs/ModelingTab";
//import { TablesTab } from "../../../AllEquipments/CommonFeatures/SidePanel/TablesTab/TablesTable";

import {
  StructureModelingProvider,
  useStructureModeling,
} from "./structureModelingContext";

import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

// Revision picking moved into the header row (Rev. dropdown), so the tab-strip
// selector is no longer mounted.
// import { EstimationRevisionSelector } from "../Modeling/StructureModelingTabs/EstimationTab/EstimationRevisionSelector";
import { ModelingControls } from "../../../AllEquipments/CommonFeatures/SidePanel/ModelingControls";
import { SpecHeader } from "../Modeling/StructureModelingTabs/SpecTab/SpecHeader";

//import Validation from "../../Piping/MainTabs/Validation";
import CompareRevisionsButton from "../../../AllEquipments/CommonFeatures/ElectronButtons/CompareRevisionsButton";

// IMPORTANT:
// Use a structural version of this component if your Piping one depends on usePiping().
// Do NOT import the Piping combo if it internally calls usePiping().
import LoadProjectDataComboBox from "./LoadProjectDataComboBox";
import { getPlantById } from "./api";
import useEmsTreeAvailable from "../../../../../hooks/useEmsTreeAvailable";

const NEW_REVISION_OPTION = "__new";

const structureProjects = [
  { id: "5", name: "Estimation BOM" },
  { id: "6", name: "3D BOM" },
  { id: "7", name: "2D BOM" },
];

const compactComboSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 20,
    height: 25,
    padding: "0 32px 0 8px !important",
    fontSize: 12,
  },
  "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
    padding: "0 !important",
    height: 25,
    lineHeight: "25px",
    fontSize: 12,
  },
  "& .MuiInputLabel-root": {
    fontSize: 12,
    transform: "translate(14px, 5px) scale(1)",
  },
  "& .MuiInputLabel-shrink": {
    transform: "translate(14px, -9px) scale(0.75)",
  },
  "& .MuiAutocomplete-endAdornment": {
    top: "50%",
    transform: "translateY(-50%)",
  },
};

function HeaderField({ label, children, minWidth = 160 }) {
  return (
    <Stack spacing={0.25} sx={{ minWidth }}>
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        sx={{
          whiteSpace: "nowrap",
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {label}
      </Typography>
      {children}
    </Stack>
  );
}

function ProjectSelectionHeader({
  plantId,
  clientName,
  type,
  selectedProject,
  onProjectSelect,
  compareBtnRef,
  isSelectionEditable,
}) {
  const [plantName, setPlantName] = useState("");
  // With the EMS tree on screen it is the single place a WBS/revision gets
  // picked, so the header only reports the selection - on every tab.
  const treeOwnsSelection = useEmsTreeAvailable();
  const canEditSelection = isSelectionEditable && !treeOwnsSelection;
  const {
    saveStructuralData,
    estimationRevisions,
    confirmedEstimationRevision,
    selectEstimationRevision,
    addEstimationRevision,
    modelingData,
    setModelingData,
    selectedModelingRevision,
  } = useStructureModeling();

  // Only show a revision as selected once it has been confirmed for the
  // project currently in the WBS box — never a leftover from a previous one.
  const displayedRevision =
    confirmedEstimationRevision &&
    confirmedEstimationRevision.projectId === selectedProject?.id
      ? confirmedEstimationRevision.revision
      : null;

  const handleRevisionChange = (_event, value) => {
    if (!value || !selectedProject?.id) return;

    if (value === NEW_REVISION_OPTION) {
      addEstimationRevision();
      return;
    }

    selectEstimationRevision(value);
  };

  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  useEffect(() => {
    if (!plantId) return;

    let cancelled = false;

    getPlantById(plantId)
      .then((plant) => {
        if (!cancelled) setPlantName(plant?.name || "");
      })
      .catch((error) => {
        console.log("Failed to load plant", error);
      });

    return () => {
      cancelled = true;
    };
  }, [plantId]);

  const handleSave = async () => {
    setSaving(true);

    try {
      await saveStructuralData();
      setSnackbar({ open: true, severity: "success", message: "Saved" });
    } catch (error) {
      console.error("Failed to save structural data", error);
      setSnackbar({
        open: true,
        severity: "error",
        message: error?.message || "Failed to save",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        px: 1.5,
        py: 0.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <FolderOpenRoundedIcon fontSize="small" />
          </Stack>

          <HeaderField label="Project UID">
            <Typography variant="body2" fontWeight={600} noWrap>
              {plantName || "—"}
            </Typography>
          </HeaderField>

          <HeaderField label="WBS UID" minWidth={260}>
            {canEditSelection ? (
              <LoadProjectDataComboBox
                plantId={plantId}
                clientName={clientName}
                type={type}
                onSelect={onProjectSelect}
              />
            ) : (
              <Typography variant="body2" fontWeight={600} noWrap>
                {selectedProject?.name || selectedProject?.link || "—"}
              </Typography>
            )}
          </HeaderField>

          <HeaderField label="Rev." minWidth={110}>
            {canEditSelection ? (
              <Autocomplete
                size="small"
                fullWidth
                disabled={!selectedProject}
                options={[...estimationRevisions, NEW_REVISION_OPTION]}
                value={displayedRevision}
                getOptionLabel={(option) =>
                  option == null
                    ? ""
                    : option === NEW_REVISION_OPTION
                      ? "+ Add New Revision"
                      : String(option)
                }
                onChange={handleRevisionChange}
                sx={compactComboSx}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select revision" />
                )}
              />
            ) : (
              <Typography variant="body2" fontWeight={600} noWrap>
                {displayedRevision ?? "—"}
              </Typography>
            )}
          </HeaderField>

          <Box sx={{ alignSelf: "center", flexShrink: 0 }}>
            <CompareRevisionsButton
              ref={compareBtnRef}
              modelingData={modelingData}
              setModelingData={setModelingData}
              revKey={selectedModelingRevision}
            />
          </Box>

          <Button
            className="header-save-btn"
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={saving}
            sx={{ alignSelf: "center", flexShrink: 0 }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </Stack>
        <Box
          sx={{
            ml: "auto",
            flexShrink: 0,
            px: 2,
            py: 1,
            borderRadius: 2,
            bgcolor: "action.hover",
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 180,
            justifyContent: "center",
          }}
        >
          <FactoryIcon fontSize="small" color="primary" />
          <Box>
            <Box
              sx={{
                fontSize: "0.7rem",
                color: "text.secondary",
                lineHeight: 1,
              }}
            >
              Plant
            </Box>
            {plantName ? (
              <Box sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                {plantName}
              </Box>
            ) : (
              <Box mt={0.5}>
                <Skeleton variant="rectangular" width={160} height={22} />
              </Box>
            )}
          </Box>
          <PlantModuleNav plantId={plantId} compact />
        </Box>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

function ModelingTabs() {
  const {
    modelingData,
    setModelingData,
    selectedModelingRevision,
    loadStructuralData,
    selectedEstimationRevision,
    setSelectedProjectId,
    loadedProjectId,
    selectEstimationRevision,
    addEstimationRevision,
  } = useStructureModeling();

  const { id: projectId, plantId } = useParams();
  const [searchParams] = useSearchParams();

  const [currentTab, setCurrentTab] = useState(0);
  const [selectedProject, setSelectedProject] = useState(null);

  const compareBtnRef = useRef(null);

  const clientName = "DEMO";
  const type = "struc";

  // Nothing anywhere in the module renders until a revision is confirmed in
  // the header — the picker itself lives above the tabs, so it stays reachable.
  const hasRevision = Boolean(selectedEstimationRevision);

  const gate = (panel) =>
    hasRevision ? (
      panel
    ) : (
      <Box sx={{ p: 4, width: "100%", textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Select a WBS UID and a revision above to load this project.
        </Typography>
      </Box>
    );

  const tabs = [
    { label: "Spec", panel: gate(<SpecTab />) },
    { label: "Estimation", panel: gate(<EstimationTab />) },
    // { label: "Modeling", panel: <ModelingTab /> },
    // { label: "Tables", panel: <TablesTab tab="structural" /> },
    // {
    //   label: "Validation",
    //   panel: (
    //     <Validation
    //       useGivenContext={useStructureModeling}
    //       projects={structureProjects}
    //     />
    //   ),
    // },
  ];

  function handleChange(newValue) {
    console.log("Tab changed to:", newValue);
    setCurrentTab(newValue);
  }

  async function handleProjectSelect(project) {
    console.log("selected structural project", project);

    setSelectedProject(project);

    const selectedId = project?.id || project?.projectId || project?.value;

    if (!selectedId) {
      console.warn(
        "Selected project does not have id/projectId/value:",
        project,
      );
      return;
    }

    await loadStructuralData(selectedId);
  }

  // Deep link from the EMS tree ("Rev N" nodes, and "+ Add revision", under a
  // Structure WBS): /plant/:plantId/estimation?projectId=&revision=&projectName=
  // (plus newRevision=1 to branch a fresh revision) opens straight into that
  // project the same way the header dropdowns used to.
  const [pendingDeepLink, setPendingDeepLink] = useState(null);
  const appliedDeepLinkRef = useRef(null);

  useEffect(() => {
    const deepLinkProjectId = searchParams.get("projectId");
    if (!deepLinkProjectId) return;

    const revision = searchParams.get("revision");
    const wantsNew = searchParams.get("newRevision") === "1";
    // Keyed on the whole intent, not just the project: picking Rev 2 and then
    // Rev 3 on the same WBS are two different links to act on.
    const key = `${deepLinkProjectId}|${revision}|${wantsNew}`;
    if (appliedDeepLinkRef.current === key) return;
    appliedDeepLinkRef.current = key;

    setSelectedProject({
      id: deepLinkProjectId,
      name: searchParams.get("projectName") || "",
    });
    // The context loads on selectedProjectId - going through
    // loadStructuralData here as well would race two loads against each other.
    // Re-picking the project already loaded is a no-op, which is what makes
    // switching revision within it instant.
    setSelectedProjectId(deepLinkProjectId);
    setPendingDeepLink({ projectId: deepLinkProjectId, revision, wantsNew });
  }, [searchParams, setSelectedProjectId]);

  // Applied only once the fetch has landed: parseStructuralData clears the
  // revision selection, so anything picked earlier would be wiped, and the
  // branch point for a new revision wouldn't be known yet.
  useEffect(() => {
    if (!pendingDeepLink) return;
    if (String(loadedProjectId) !== String(pendingDeepLink.projectId)) return;

    setPendingDeepLink(null);

    if (pendingDeepLink.wantsNew) {
      addEstimationRevision();
    } else if (pendingDeepLink.revision != null) {
      selectEstimationRevision(pendingDeepLink.revision);
    }
  }, [
    pendingDeepLink,
    loadedProjectId,
    addEstimationRevision,
    selectEstimationRevision,
  ]);

  useEffect(() => {
    window.__menuHandlers = window.__menuHandlers || {};

    window.__menuHandlers.COMPARE = () => {
      compareBtnRef.current?.triggerOpen();
    };

    return () => {
      if (window.__menuHandlers) {
        window.__menuHandlers.COMPARE = null;
      }
    };
  }, [modelingData, setModelingData, selectedModelingRevision]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "row",
        flex: 1,
      }}
    >
      <Box sx={{ width: "100%" }}>
        <ProjectSelectionHeader
          plantId={plantId || projectId}
          clientName={clientName}
          type={type}
          selectedProject={selectedProject}
          onProjectSelect={handleProjectSelect}
          compareBtnRef={compareBtnRef}
          isSelectionEditable={currentTab === 0}
        />

        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <NavTabs
            tabs={tabs}
            navLabel="mcs-tab"
            handleOutputChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {currentTab === 0 && hasRevision && (
              <Box sx={{ flexGrow: 1 }}>
                <SpecHeader />
              </Box>
            )}

            {currentTab === 2 && (
              <Box sx={{ flexGrow: 1 }}>
                <ModelingControls />
              </Box>
            )}
          </NavTabs>
        </Paper>
      </Box>

      {/* {currentTab === 3 && (
        <Box>
          <SidePanel />
        </Box>
      )} */}
    </Box>
  );
}

export function Modeling() {
  const { projectId } = useParams();

  return (
    <StructureModelingProvider id={projectId}>
      <ModelingTabs />
    </StructureModelingProvider>
  );
}
